import { format, parseISO } from 'date-fns'
import { startOfWeek } from 'date-fns/startOfWeek'
import { endOfWeek } from 'date-fns/endOfWeek'
import { setHours } from 'date-fns/setHours'
import { getDay } from 'date-fns/getDay'
import { add } from 'date-fns/add'
import { sub } from 'date-fns/sub'
import { toZonedTime, toDate, format as formatDate } from 'date-fns-tz'
import { GENERICS as dict } from '@/resources/dict'
import * as config from '@/config'

export const { daysOfTheWeek } = dict

export const weekDayNameToWeekDayNumber = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
}

export const weekDayNumberToWeekDayName = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
]

export const addTimeZone = date => {
  const utcDate = toDate(date, { timeZone: 'UTC' })
  return toZonedTime(utcDate, config.TIMEZONE)
}

export const parseUTCDate = (date, format) => {
  const zonedDate = addTimeZone(date)

  return format
    ? formatDate(zonedDate, format, { timeZone: config.TIMEZONE })
    : zonedDate
}

/**
 * Retrieves description day based on Date object
 */
export const getDayName = date => {
  const dayNumber = getDay(parseUTCDate(date))
  return daysOfTheWeek[weekDayNumberToWeekDayName[dayNumber]]
}

/**
 * Retrieves UTC date based on string date
 * @param {String} dateStr - date string following input DatePicker format
 * @param {Array[Number]} fixedTime - array of [Hours, Minutes, Seconds, Miliseconds] to be fixed on the date
 */

/**
 * Retrieves last week UTC date based on day of the week number
 * @param {Number} weekDay - week day number, P.S. check const weekDayNameToWeekDayNumber
 * @param {Date} dateRef - date reference for going back one week, current date by default
 * @param {Array[Number]} fixedTime - array of [Hours, Minutes, Seconds, Miliseconds] to be fixed on the date
 */

export const getToday = () => new Date()

export const getYesterday = () => sub(getToday(), { days: 1 })

export const getCurrentWeekMondayDate = (date = new Date()) => {
  return startOfWeek(date, { weekStartsOn: 1 }).toISOString()
}

export const getNextWeekSundayDate = (date = new Date()) => {
  return setHours(endOfWeek(date, { weekStartsOn: 1 }), 0).toISOString()
}

export const removeTimeStamp = dateStr => dateStr.split('T')[0]

export const toISOStringWithOffset = (date, includeMs = false) => {
  const d = typeof date === 'string' ? new Date(date) : date
  const iso = d.toISOString()
  return includeMs ? iso.replace('Z', '+00:00') : iso.slice(0, 19) + '+00:00'
}

export const addHoursToISOStringWithOffset = (
  date,
  hours = 1,
  includeMs = false
) => {
  const d = typeof date === 'string' ? new Date(date) : date
  const dateWithHours = add(d, { hours })
  return toISOStringWithOffset(dateWithHours, includeMs)
}

export const isMidnight = endDate => {
  return endDate && endDate.getHours() === 0 && endDate.getMinutes() === 0
}

export const handleEndDate = endDate => {
  if (isMidnight(endDate)) {
    endDate.setHours(23, 59, 59)
  }
  return endDate || undefined
}

export const obtainHoursAndMinutes = (fullHour = '00:00:00') => {
  const INITIAL_INDEX = 0
  const SECONDS_INDEX = 2

  return fullHour.split(':').slice(INITIAL_INDEX, SECONDS_INDEX).join(':')
}

export const isToday = date =>
  new Date().toDateString() === addTimeZone(date).toDateString()

export const isTomorrow = date => {
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  return tomorrow.toDateString() === addTimeZone(date).toDateString()
}

export const getDayNumber = date => date.getDate()

export const getHour = date => date.getHours()

export const getShortDate = date => {
  if (!date) return null

  return format(parseISO(date), 'dd/MM')
}

export const getLongDate = date => {
  if (!date) return null

  return format(parseISO(date), 'dd/MM/yy')
}

export const millisecondsToSeconds = milliseconds => {
  return milliseconds / 1000
}

export const millisecondsToMinutes = milliseconds => {
  return milliseconds / (60 * 1000)
}

export const getCurrentMonth = {
  0: dict.months.january,
  1: dict.months.february,
  2: dict.months.march,
  3: dict.months.april,
  4: dict.months.may,
  5: dict.months.june,
  6: dict.months.july,
  7: dict.months.august,
  8: dict.months.september,
  9: dict.months.october,
  10: dict.months.november,
  11: dict.months.december,
}
